import { useCallback, useState } from 'react';
import { API_BASE } from '../../../config/constants';

export type RegistrationInput = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    companyId?: string | number;
};

function buildJsonHeaders(): Record<string, string> {
    return { 'Content-Type': 'application/json' };
}

async function hashPassword(plainTextPassword: string): Promise<string> {
    const encoded = new TextEncoder().encode(plainTextPassword);
    const digest = await crypto.subtle.digest('SHA-256', encoded);
    const bytes = Array.from(new Uint8Array(digest));
    return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function postRegisterUser(input: RegistrationInput, passwordToSend: string) {
    const response = await fetch(`${API_BASE}/api/users/register-user`, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({
            user: {
                email: input.email.trim(),
                password: passwordToSend,
                first_name: input.firstName.trim(),
                last_name: input.lastName.trim(),
                company_id: input.companyId ? String(input.companyId) : undefined,
            },
        }),
    });

    const payload = await response.json().catch(() => ({}));
    return { response, payload };
}

export function useHRegistreation() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const clearMessages = useCallback(() => {
        setError(null);
        setSuccess(null);
    }, []);

    const registerUser = useCallback(
        async (input: RegistrationInput) => {
            clearMessages();
            setLoading(true);

            try {
                if (!navigator.onLine) {
                    throw new Error('No internet connection. Please try again.');
                }

                const hashedPassword = await hashPassword(input.password);

                // Dart flow hashes password first. Some backend deployments may expect plain text,
                // so we fallback to plain password if the hashed attempt is rejected.
                let { response, payload } = await postRegisterUser(input, hashedPassword);

                if (!response.ok || !payload?.success) {
                    const fallback = await postRegisterUser(input, input.password);
                    if (fallback.response.ok && fallback.payload?.success) {
                        response = fallback.response;
                        payload = fallback.payload;
                    }
                }

                if (!response.ok || !payload?.success) {
                    const backendMessage = payload?.message || payload?.detail || payload?.error;
                    throw new Error(backendMessage || 'Registration failed. Please try again.');
                }

                setSuccess('User registered successfully! You can now log in.');
                return { success: true, payload };
            } catch (err: any) {
                const message = err?.message || 'Registration failed. Please try again.';
                setError(message);
                return { success: false, message };
            } finally {
                setLoading(false);
            }
        },
        [clearMessages]
    );

    return {
        loading,
        error,
        success,
        setError,
        setSuccess,
        clearMessages,
        registerUser,
        'register-user': registerUser,
    };
}

export const useHRegistration = useHRegistreation;
